import { useEffect, useRef, useState } from "react";

import type { RealtimeDiagnosis, SourceHealth, SourcesDelta } from "../contract/types";
import { createSnapshotSync } from "../api/stream";
import { useBackend } from "./BackendProvider";
import { useStreamContext } from "./StreamProvider";
import { useNetworkId } from "../config/ConfigProvider";

interface LiveSourcesState {
  sources: SourceHealth[];
  /**
   * D-020 — verdict global temps réel + battements de cœur ingestion/moteur.
   * REST uniquement (absent du delta SSE `sources`) : rafraîchi à chaque
   * re-instantané et toutes les 45 s. `null` tant que le 1er `GET` n'a pas répondu.
   */
  realtime: RealtimeDiagnosis | null;
  loading: boolean;
  error: unknown;
}

/**
 * Santé des sources en direct (F4.1, CDC §2.3) : instantané `/sources/health`
 * puis sujet `sources` du flux. Le payload de delta remplace la liste entière
 * (liste courte) — pas un `CollectionDelta`, d'où la synchro dédiée ici.
 */
export function useLiveSources(): LiveSourcesState {
  const backend = useBackend();
  const networkId = useNetworkId();
  const { stream } = useStreamContext();
  const [state, setState] = useState<LiveSourcesState>({
    sources: [],
    realtime: null,
    loading: true,
    error: null,
  });
  const deps = useRef({ backend, networkId });
  deps.current = { backend, networkId };

  useEffect(() => {
    if (!stream) return;
    let alive = true;

    const pullRealtime = async () => {
      try {
        const snap = await deps.current.backend.api.getSourcesHealth(deps.current.networkId);
        if (alive) setState((s) => ({ ...s, realtime: snap.realtime }));
      } catch {
        /* on garde le dernier verdict connu */
      }
    };

    const sync = createSnapshotSync<SourceHealth[], SourcesDelta>({
      topic: "sources",
      stream,
      loadSnapshot: async (signal) => {
        const snap = await deps.current.backend.api.getSourcesHealth(deps.current.networkId, signal);
        if (alive) setState((s) => ({ ...s, realtime: snap.realtime }));
        return snap.seq === undefined ? { state: snap.sources } : { state: snap.sources, seq: snap.seq };
      },
      applyDelta: (_prev, payload) => payload.sources,
      onState: (sources) => setState((s) => ({ ...s, sources, loading: false, error: null })),
      onError: (error) => setState((s) => ({ ...s, loading: false, error })),
    });

    const timer = setInterval(pullRealtime, 45_000);
    return () => {
      alive = false;
      clearInterval(timer);
      sync.dispose();
    };
  }, [stream, networkId]);

  return state;
}
